import React, { useState, useEffect } from 'react';
import { Home, Users, Briefcase, Settings, LogOut, User, UserCheck, ArrowLeft, FileText, Link2, X, Calendar, Database, PhoneCall, Mail, RefreshCw, ClipboardList, Bell } from 'lucide-react';
import authService from '../services/authService';
import { tokenManager } from '../services/api';
import AdminService from '../services/adminService';
import QuickBooksService from '../services/quickbooksService';
import userService from '../services/userService';
import { useToast } from './Toast';

const Sidebar = ({ currentView, onNavigate, onLogout, userEmail, userProfile, isMobileOpen, onMobileClose, overdueRemindersCount = 0 }) => {
  const toast = useToast();
  const [qbConnection, setQbConnection] = useState(null);

  // Show notification via shared toast
  const showNotification = (message, type = 'success') => {
    if (type === 'error') toast.error(message);
    else toast.success(message);
  };

  // Check QuickBooks connection for franchisees
  const checkQBConnection = async () => {
    if (userProfile?.role === 'franchisee' && userProfile?.id) {
      try {
        const connectionInfo = await QuickBooksService.getConnectionInfo(userProfile.id);
        if (connectionInfo) {
          setQbConnection({
            connected: true,
            environment: userProfile.qb_environment || 'production',
            companyId: connectionInfo.company_id
          });
        } else {
          setQbConnection({ connected: false });
        }
      } catch (error) {
        // If there's an error (like no connection), set as not connected
        setQbConnection({ connected: false });
      }
    } else {
      setQbConnection(null); // Not a franchisee, don't show QB info
    }
  };

  // Load QB connection when component mounts or user profile changes
  useEffect(() => {
    checkQBConnection();
  }, [userProfile]);

  // Switch User dropdown state (admin only)
  const [switchUserGroups, setSwitchUserGroups] = useState([]);
  const [switchingUser, setSwitchingUser] = useState(false);

  const isAdminSession = userProfile?.role === 'admin' || authService.hasAdminReturn();

  useEffect(() => {
    const fetchSwitchUsers = async () => {
      if (!isAdminSession) return;

      // When impersonating, try cached groups first (sessionStorage is per-tab)
      if (authService.hasAdminReturn()) {
        try {
          const cached = sessionStorage.getItem('pestcrm_switchUserGroups');
          if (cached) {
            setSwitchUserGroups(JSON.parse(cached));
            return;
          }
        } catch (e) { /* ignore */ }
        // Cache miss (new tab) — temporarily use admin token to fetch users
        try {
          const adminSession = JSON.parse(localStorage.getItem('admin_return_session'));
          if (!adminSession?.token) return;
          const currentToken = tokenManager.getToken();
          const currentRefresh = tokenManager.getRefreshToken();
          tokenManager.setTokens(adminSession.token, adminSession.refreshToken);
          try {
            const [franchiseeRes, techRes] = await Promise.all([
              userService.getUsers({ role: 'franchisee', limit: 100, active: true }),
              userService.getUsers({ role: 'technician', limit: 100, active: true })
            ]);
            const franchisees = franchiseeRes.users || [];
            const techs = techRes.users || [];
            const groups = franchisees.map(f => ({
              id: f.id,
              name: f.name,
              company: f.company_name || '',
              technicians: techs
                .filter(t => t.assigned_franchisee_id === f.id)
                .map(t => ({ id: t.id, name: t.name }))
            }));
            setSwitchUserGroups(groups);
            try { sessionStorage.setItem('pestcrm_switchUserGroups', JSON.stringify(groups)); } catch (e) { /* ignore */ }
          } finally {
            // Always restore impersonated user's token
            tokenManager.setTokens(currentToken, currentRefresh);
          }
        } catch (e) {
          console.error('Error fetching switch users with admin token:', e);
        }
        return;
      }

      try {
        const [franchiseeRes, techRes] = await Promise.all([
          userService.getUsers({ role: 'franchisee', limit: 100, active: true }),
          userService.getUsers({ role: 'technician', limit: 100, active: true })
        ]);
        const franchisees = franchiseeRes.users || [];
        const techs = techRes.users || [];

        const groups = franchisees.map(f => ({
          id: f.id,
          name: f.name,
          company: f.company_name || '',
          technicians: techs
            .filter(t => t.assigned_franchisee_id === f.id)
            .map(t => ({ id: t.id, name: t.name }))
        }));
        setSwitchUserGroups(groups);
        try {
          sessionStorage.setItem('pestcrm_switchUserGroups', JSON.stringify(groups));
        } catch (e) { /* ignore */ }
      } catch (error) {
        console.error('Error fetching users for switch dropdown:', error);
      }
    };
    fetchSwitchUsers();
  }, [userProfile]);

  const handleSwitchUser = async (userId) => {
    if (!userId || userId === 'current') return;
    const allUsers = switchUserGroups.flatMap(g => [
      { id: g.id, name: g.name },
      ...g.technicians
    ]);
    const targetUser = allUsers.find(u => String(u.id) === String(userId));
    if (!targetUser) return;

    try {
      setSwitchingUser(true);
      // If currently impersonating, restore admin session first
      if (authService.hasAdminReturn()) {
        await authService.returnToAdmin();
      }
      await authService.loginAsUser(targetUser);
      window.location.reload();
    } catch (error) {
      console.error('Error switching user:', error);
      setSwitchingUser(false);
    }
  };

  // Check if currently impersonating a user
  const impersonationData = localStorage.getItem('admin_impersonation');
  const isImpersonating = impersonationData ? JSON.parse(impersonationData) : null;

  const handleReturnToAdmin = async () => {
    if (!isImpersonating) return;
    
    try {
      // Check if this is a backend-based impersonation with return token
      if (isImpersonating.impersonationType === 'admin_backend' && isImpersonating.adminReturnToken) {
        try {
          // Use passwordless return with return token
          const returnResult = await AdminService.returnToAdmin(isImpersonating.adminReturnToken);
          
          // Store the admin token and update auth state
          authService.setToken(returnResult.token);
          
          // Remove impersonation data
          localStorage.removeItem('admin_impersonation');
          
          console.log('Successfully returned to admin account');
          // Reload the page to refresh auth state
          window.location.reload();
          return; // Exit early on success
        } catch (tokenError) {
          console.warn('Return token failed, falling back to password:', tokenError.message);
          // Fall through to password-based return
        }
      }
      
      // Handle backend impersonation (either no token or token failed)
      if (isImpersonating.impersonationType === 'admin_backend') {
        // Backend impersonation without return token (fallback to password)
        const adminPassword = prompt(`Enter admin password for ${isImpersonating.adminName}:`);
        if (!adminPassword) return;
        
        // Log the end of impersonation session
        try {
          await AdminService.endImpersonation();
        } catch (endError) {
          console.warn('Could not log end of impersonation:', endError);
          // Continue anyway since this is just logging
        }
        
        // Login with admin credentials
        await authService.login(isImpersonating.adminEmail, adminPassword);
        localStorage.removeItem('admin_impersonation');
      } else {
        // Legacy impersonation fallback
        const adminPassword = prompt(`Enter admin password for ${isImpersonating.adminName}:`);
        if (!adminPassword) return;
        
        await authService.login(isImpersonating.adminEmail, adminPassword);
        localStorage.removeItem('admin_impersonation');
      }
    } catch (error) {
      console.error('Error returning to admin:', error);
      showNotification('Failed to return to admin account: ' + error.message, 'error');
    }
  };
  const baseMenuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'customers', label: 'Customers', icon: Users },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'emails', label: 'Emails', icon: Mail },
    { id: 'jobs', label: 'Jobs', icon: Briefcase },
    { id: 'appointments', label: 'Appointments', icon: ClipboardList },
    { id: 'reminders', label: 'Reminders', icon: Bell },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  // Add menu items based on role
  const getUsersMenuLabel = () => {
    if (userProfile?.role === 'admin') return 'Users';
    if (userProfile?.role === 'franchisee') return 'Technicians';
    return 'Users';
  };

  // Build menu items based on role
  let menuItems = [...baseMenuItems];
  
  if (userProfile?.role === 'admin') {
    // Insert Canvassing Data and Users before Settings
    menuItems = [
      ...menuItems.slice(0, -1), // Everything except Settings
      { id: 'canvassing', label: 'Canvassing Data', icon: Database },
      { id: 'users', label: getUsersMenuLabel(), icon: UserCheck },
      ...menuItems.slice(-1) // Settings at the end
    ];
  } else if (userProfile?.role === 'franchisee') {
    // Insert Canvassing and Users before Settings
    menuItems = [
      ...menuItems.slice(0, -1), // Everything except Settings
      { id: 'canvassing-mobile', label: 'Canvassing', icon: PhoneCall },
      { id: 'users', label: getUsersMenuLabel(), icon: UserCheck },
      ...menuItems.slice(-1) // Settings at the end
    ];
  } else if (userProfile?.role === 'technician') {
    // Technicians get Canvassing before Settings
    menuItems = [
      ...menuItems.slice(0, -1), // Everything except Settings
      { id: 'canvassing-mobile', label: 'Canvassing', icon: PhoneCall },
      ...menuItems.slice(-1) // Settings at the end
    ];
  }

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden md:flex w-64 bg-gradient-to-b from-pest-blue to-pest-teal text-white shadow-lg flex-col h-screen sticky top-0">
        <div className="px-4 pt-4 pb-2 flex-1 overflow-y-auto">
          <div className="flex justify-center mb-4">
            <div className="bg-white px-3 py-2 rounded-lg shadow-sm w-full">
              <img src="/logo.png" alt="Pest Control" className="w-full h-auto max-h-16 mx-auto" />
            </div>
          </div>

          {isImpersonating && (
            <div className="bg-orange-500 bg-opacity-20 border border-orange-400 rounded-lg p-3 mb-6">
              <div className="text-center">
                <div className="text-xs text-orange-100 mb-2">
                  <strong>👤 Viewing as:</strong>
                </div>
                <div className="text-sm font-semibold text-white mb-2">
                  {isImpersonating.targetUser.name}
                </div>
                <button
                  onClick={handleReturnToAdmin}
                  className="w-full flex items-center justify-center space-x-2 bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 rounded-lg text-xs transition-colors duration-200"
                >
                  <ArrowLeft size={14} />
                  <span>Switch back to {isImpersonating.adminName}</span>
                </button>
              </div>
            </div>
          )}
          
          <nav className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentView === item.id ||
                (item.id === 'customers' && currentView === 'customer-details') ||
                (item.id === 'invoices' && currentView === 'invoice-details') ||
                (item.id === 'jobs' && currentView === 'job-details') ||
                (item.id === 'calendar' && currentView === 'calendar');

              return (
                <button
                  key={item.id}
                  onClick={() => onNavigate(item.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors duration-200 ${
                    isActive
                      ? 'bg-white bg-opacity-20 text-white'
                      : 'text-blue-100 hover:bg-white hover:bg-opacity-10 hover:text-white'
                  }`}
                >
                  <Icon size={20} />
                  <span className="font-medium">{item.label}</span>
                  {item.id === 'reminders' && overdueRemindersCount > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center ml-auto">
                      {overdueRemindersCount}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
        
        <div className="p-4 space-y-3 flex-shrink-0 overflow-y-auto max-h-[45vh]">
          {/* Switch User dropdown - admin only */}
          {isAdminSession && switchUserGroups.length > 0 && (
            <div className="bg-white bg-opacity-10 rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-2">
                <RefreshCw size={14} className="text-blue-100" />
                <span className="text-xs font-medium text-blue-100">Switch User</span>
              </div>
              <select
                value={authService.hasAdminReturn() ? String(userProfile?.id) : 'admin'}
                onChange={(e) => {
                  if (e.target.value === 'admin') {
                    if (authService.hasAdminReturn()) {
                      authService.returnToAdmin().then(() => window.location.reload());
                    }
                  } else {
                    handleSwitchUser(e.target.value);
                  }
                }}
                disabled={switchingUser}
                className="w-full border border-white border-opacity-30 rounded-lg px-2 py-1.5 text-xs text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
              >
                <option value="admin">System Administrator</option>
                {switchUserGroups.map(group => (
                  <optgroup key={group.id} label={group.company || group.name}>
                    <option value={group.id}>{group.name}</option>
                    {group.technicians.map(tech => (
                      <option key={tech.id} value={tech.id}>
                        {tech.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          )}

          {userEmail && (
            <div className="bg-white bg-opacity-10 rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-1">
                <User size={16} className="text-blue-100" />
                <span className="text-xs text-blue-100 truncate">{userProfile?.name || userEmail}</span>
              </div>
              {userProfile?.role && (
                <div className="mb-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    userProfile.role === 'admin'
                      ? 'bg-red-500 text-white'
                      : userProfile.role === 'franchisee'
                      ? 'bg-purple-500 text-white'
                      : 'bg-green-500 text-white'
                  }`}>
                    {userProfile.role}
                  </span>
                </div>
              )}

              {/* Return to Admin button - show when admin is logged in as another user */}
              {authService.hasAdminReturn() && userProfile?.role !== 'admin' && (
                <button
                  onClick={async () => {
                    try {
                      await authService.returnToAdmin();
                      window.location.reload();
                    } catch (error) {
                      console.error('Return to admin error:', error);
                    }
                  }}
                  className="w-full flex items-center justify-center space-x-2 bg-amber-600 hover:bg-amber-700 text-white px-3 py-2 rounded-lg text-sm transition-colors duration-200 mb-2"
                >
                  <ArrowLeft size={14} />
                  <span>Return to Admin</span>
                </button>
              )}

              <button
                onClick={onLogout}
                className="w-full flex items-center justify-center space-x-2 bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm transition-colors duration-200"
              >
                <LogOut size={14} />
                <span>Sign Out</span>
              </button>
            </div>
          )}

          {/* QuickBooks Environment Indicator */}
          {qbConnection?.connected && (
            <div className="bg-white bg-opacity-10 rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-2">
                <Link2 size={14} className="text-green-400" />
                <img src="/quickbooks-logo.svg" alt="QuickBooks" className="w-4 h-4" />
                <span className="text-xs font-medium text-blue-100">QuickBooks</span>
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-blue-200">Environment:</span>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    qbConnection.environment === 'sandbox'
                      ? 'bg-orange-500 text-white'
                      : 'bg-green-500 text-white'
                  }`}>
                    {qbConnection.environment === 'sandbox' ? 'Sandbox' : 'Production'}
                  </span>
                </div>
                {qbConnection.companyId && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-blue-200">Company ID:</span>
                    <span className="text-xs text-blue-100 font-mono">
                      {qbConnection.companyId}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="bg-white bg-opacity-10 rounded-lg p-3">
            <p className="text-xs text-blue-100 text-center">
              PestCRM v1.2
            </p>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Drawer */}
      <div className={`fixed inset-y-0 left-0 z-30 w-80 bg-gradient-to-b from-pest-blue to-pest-teal text-white transform transition-transform duration-300 ease-in-out md:hidden ${
        isMobileOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Mobile Header */}
          <div className="flex items-center justify-between p-4 border-b border-white border-opacity-20">
            <div className="bg-white px-3 py-2 rounded-lg shadow-sm">
              <img src="/logo.png" alt="Pest Control" className="h-8 w-auto" />
            </div>
            <button
              onClick={onMobileClose}
              className="p-2 rounded-lg hover:bg-white hover:bg-opacity-10 transition-colors"
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {isImpersonating && (
              <div className="bg-orange-500 bg-opacity-20 border border-orange-400 rounded-lg p-3 mb-6">
                <div className="text-center">
                  <div className="text-xs text-orange-100 mb-2">
                    <strong>👤 Viewing as:</strong>
                  </div>
                  <div className="text-sm font-semibold text-white mb-2">
                    {isImpersonating.targetUser.name}
                  </div>
                  <button
                    onClick={handleReturnToAdmin}
                    className="w-full flex items-center justify-center space-x-2 bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 rounded-lg text-xs transition-colors duration-200"
                  >
                    <ArrowLeft size={14} />
                    <span>Switch back to {isImpersonating.adminName}</span>
                  </button>
                </div>
              </div>
            )}
            
            <nav className="space-y-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = currentView === item.id ||
                  (item.id === 'customers' && currentView === 'customer-details') ||
                  (item.id === 'invoices' && currentView === 'invoice-details') ||
                  (item.id === 'jobs' && currentView === 'job-details') ||
                  (item.id === 'calendar' && currentView === 'calendar');
                
                return (
                  <button
                    key={item.id}
                    onClick={() => onNavigate(item.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200 ${
                      isActive
                        ? 'bg-white bg-opacity-20 text-white'
                        : 'text-blue-100 hover:bg-white hover:bg-opacity-10 hover:text-white'
                    }`}
                  >
                    <Icon size={20} />
                    <span className="font-medium">{item.label}</span>
                    {item.id === 'reminders' && overdueRemindersCount > 0 && (
                      <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center ml-auto">
                        {overdueRemindersCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
          
          {/* Mobile Footer */}
          <div className="p-4 space-y-3 border-t border-white border-opacity-20">
            {/* Switch User dropdown - admin only (mobile) */}
            {isAdminSession && switchUserGroups.length > 0 && (
              <div className="bg-white bg-opacity-10 rounded-lg p-3">
                <div className="flex items-center space-x-2 mb-2">
                  <RefreshCw size={14} className="text-blue-100" />
                  <span className="text-xs font-medium text-blue-100">Switch User</span>
                </div>
                <select
                  value={authService.hasAdminReturn() ? String(userProfile?.id) : 'admin'}
                  onChange={(e) => {
                    if (e.target.value === 'admin') {
                      if (authService.hasAdminReturn()) {
                        authService.returnToAdmin().then(() => window.location.reload());
                      }
                    } else {
                      handleSwitchUser(e.target.value);
                    }
                  }}
                  disabled={switchingUser}
                  className="w-full border border-white border-opacity-30 rounded-lg px-2 py-1.5 text-xs text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
                >
                  <option value="admin">System Administrator</option>
                  {switchUserGroups.map(group => (
                    <optgroup key={group.id} label={group.company || group.name}>
                      <option value={group.id}>{group.name}</option>
                      {group.technicians.map(tech => (
                        <option key={tech.id} value={tech.id}>
                          {tech.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            )}

            {userEmail && (
              <div className="bg-white bg-opacity-10 rounded-lg p-3">
                <div className="flex items-center space-x-2 mb-1">
                  <User size={16} className="text-blue-100" />
                  <span className="text-xs text-blue-100 truncate">{userProfile?.name || userEmail}</span>
                </div>
                {userProfile?.role && (
                  <div className="mb-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      userProfile.role === 'admin'
                        ? 'bg-red-500 text-white'
                        : userProfile.role === 'franchisee'
                        ? 'bg-purple-500 text-white'
                        : 'bg-green-500 text-white'
                    }`}>
                      {userProfile.role}
                    </span>
                  </div>
                )}

                {/* Return to Admin button - show when admin is logged in as another user */}
                {authService.hasAdminReturn() && userProfile?.role !== 'admin' && (
                  <button
                    onClick={async () => {
                      try {
                        await authService.returnToAdmin();
                        window.location.reload();
                      } catch (error) {
                        console.error('Return to admin error:', error);
                      }
                    }}
                    className="w-full flex items-center justify-center space-x-2 bg-amber-600 hover:bg-amber-700 text-white px-3 py-2 rounded-lg text-sm transition-colors duration-200 mb-2"
                  >
                    <ArrowLeft size={14} />
                    <span>Return to Admin</span>
                  </button>
                )}

                <button
                  onClick={onLogout}
                  className="w-full flex items-center justify-center space-x-2 bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm transition-colors duration-200"
                >
                  <LogOut size={14} />
                  <span>Sign Out</span>
                </button>
              </div>
            )}

            {/* QuickBooks Environment Indicator */}
            {qbConnection?.connected && (
              <div className="bg-white bg-opacity-10 rounded-lg p-3">
                <div className="flex items-center space-x-2 mb-2">
                  <Link2 size={14} className="text-green-400" />
                  <img src="/quickbooks-logo.svg" alt="QuickBooks" className="w-4 h-4" />
                  <span className="text-xs font-medium text-blue-100">QuickBooks</span>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-blue-200">Environment:</span>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      qbConnection.environment === 'sandbox'
                        ? 'bg-orange-500 text-white'
                        : 'bg-green-500 text-white'
                    }`}>
                      {qbConnection.environment === 'sandbox' ? 'Sandbox' : 'Production'}
                    </span>
                  </div>
                  {qbConnection.companyId && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-blue-200">Company ID:</span>
                      <span className="text-xs text-blue-100 font-mono">
                        {qbConnection.companyId}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="bg-white bg-opacity-10 rounded-lg p-3">
              <p className="text-xs text-blue-100 text-center">
                PestCRM v1.2
              </p>
            </div>
          </div>
        </div>
      </div>

    </>
  );
};

export default Sidebar;